/**
 * HIS V4 · Page Presence types
 * (shape เดียวกับ his/core/presence ของ backend — ดู ishealth-platform/docs/presence-plan.md)
 */

import type * as React from "react";

export type PresenceStateValue = "OPEN" | "ACTIVE" | "TYPING";

export type PresencePeer = {
  sid: string;
  user_id?: number | null;
  /** ชื่อบัญชีผู้ใช้จาก backend (user.get_full_name()) — ไม่ใช่ข้อมูลที่ client แต่งเอง */
  name: string;
  role?: string;
  division_id?: number | null;
  page_key?: string;
  state: PresenceStateValue;
  /** id ของ input ที่กำลังพิมพ์ (เช่น hx_cc) — null เมื่อไม่ได้พิมพ์ */
  field?: string | null;
  /** epoch seconds (UTC) —ครั้งแรกที่ opens หน้านี้ (reset เมื่อ session หมดอายุ) */
  opened_at: number;
  joined_at?: number;
  last_seen: number;
  seconds_open: number;
  /** tab ปัจจุบันของตัวเอง */
  self: boolean;
  /** ผู้ใช้คนเดียวกัน แต่เปิดอีกหน้า/อีก tab */
  own: boolean;
  meta?: Record<string, unknown> | null;
};

/** ค่าที่ backend ส่งกลับใน ทุก response — frontend ต้องใช้ค่าเหล่านี้ ห้าม hardcode */
export type PresenceConfig = {
  enabled: boolean;
  ttl_sec: number;
  heartbeat_ms: number;
  typing_throttle_ms: number;
  poll_ms: number;
  broadcast_max_age_sec: number;
  max_peers: number;
};

export const DEFAULT_PRESENCE_CONFIG: PresenceConfig = {
  enabled: false,
  ttl_sec: 60,
  heartbeat_ms: 15000,
  typing_throttle_ms: 2000,
  poll_ms: 10000,
  broadcast_max_age_sec: 30,
  max_peers: 50,
};

export type PresencePeersResult = {
  page_key: string;
  rev: number;
  peers: PresencePeer[];
  truncated?: boolean;
  server_now?: number;
  cfg?: PresenceConfig;
  self?: PresencePeer;
};

export type PresenceChannel = "startup" | "ws" | "poll" | "off";

export type PresenceStatus = {
  /** ws = ได้รับ delta จาก websocket · poll = ใช้ REST polling เป็นหลัก (fallback 10 วินาที) */
  channel: PresenceChannel;
  wsConnected: boolean;
  /** epoch ms ของครั้งสุดท้ายที่ยืนยันว่า peers ตรงกับ backend แล้ว */
  syncedAt: number;
  error?: string;
};

export const PEER_STATES: PresenceStateValue[] = ["OPEN", "ACTIVE", "TYPING"];

export const isTyping = (peer: PresencePeer): boolean => peer.state === "TYPING";

/** peer ที่ควรแสดงบนแถบ = ทุกคนที่ไม่ใช่ tab ปัจจุบันของตัวเอง */
export const isOtherPeer = (peer: PresencePeer): boolean => !peer.self;

/**
 * สิ่งที่ shell ของทุกหน้า (PatientPanelV4) ต้องได้:
 *   bar      = แถบแจ้งเตือน (undefined = ไม่แสดงแถบ)
 *   setActive/setTyping = shell เรียกเมื่อผู้ใช้ focus/พิมพ์ ใน main ของหน้า
 */
export type PresencePanelV4Binding = {
  bar?: React.ReactNode;
  setActive: () => void;
  setTyping: (field: string) => void;
};
